import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { SsApiService } from './api/ss-api.service';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { FormControl, Validators } from '@angular/forms';
import { UserService } from './user/user.service';
import { ToastMessageService } from '../shared/modules/toast-message/toast-message.service';
import { FormsErrorHandlerService } from './forms-error-handler.service';
import { ModalService } from '../modal/modal.service';
import { TranslationService } from './translation/translation.service';

@Injectable({
  providedIn: 'root'
})
export class TwoFactorService {
  private _api = inject(SsApiService);
  private _toastMessage = inject(ToastMessageService);
  private _formErrors = inject(FormsErrorHandlerService);
  private _modals = inject(ModalService);
  private _translate = inject(TranslationService);
  user = inject(UserService);


  /**
   * Two-factor auth data
   */
  public twoFactorData$: Observable<any> = this._api.getPlayerTwoFactor();

  /**
   * Two-factor auth enabled/disabled state subject
   */
  public twoFactorEnabled$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /**
   * Two-factor auth code control
   */
  public codeControl: FormControl = new FormControl('', Validators.required);

  /**
   * Access to state two-factor subject
   */
  get twoFactorEnabled() {
    return this.twoFactorEnabled$.getValue();
  }

  /**
   * Enable two-factor auth
   * @param otp_secret
   */
  onEnableTwoFactor(otp_secret: string) {

    if (this.codeControl.invalid) {
      return;
    }

    const data = {
      two_factor: {
        otp_secret,
        authentication_code: this.codeControl.value
      }
    };

    this._api.postPlayerTwoFactor(data).pipe(
      tap((response) => {
        this._toastMessage.success('t.2fa-on');
        this._showBackupCodesModal(response);
        this._resetCodeControl();
      }),
      catchError((error) => {
        this._toastMessage.error(this._formErrors.ssBackendErrorsToArray(error.error)[0] || 't.something-went-wrong');
        return of(error);
      }),
      switchMap(() => this.user.getUserInfo())
    ).subscribe();
  }

  /**
   * Disable two-factor auth
   */
  onDisableTwoFactor() {
    if (this.codeControl.invalid) {
      return;
    }

    const data = {
      two_factor: {
        authentication_code: this.codeControl.value
      }
    };

    this._api.deletePlayerTwoFactor(data).pipe(
      tap(() => {
        this._toastMessage.success('t.2fa-off');
        this._resetCodeControl();
      }),
      catchError((error) => {
        this._toastMessage.error(this._formErrors.ssBackendErrorsToArray(error.error)[0] || 't.something-went-wrong');
        return of(error);
      }),
      switchMap(() => this.user.getUserInfo())
    ).subscribe();
  }

  /**
   * Reset form control
   */
  private _resetCodeControl() {
    this.codeControl.reset();
    this.codeControl.markAsUntouched();
    this.codeControl.markAsPristine();
  }

  /**
   * Show backup codes modal
   * @param response
   * @private
   */
  private _showBackupCodesModal(response) {
    this._modals.open('TEXT_ALERT', {
      template: 'SMALL',
      data: {
        text: this._translate.translate('t.backup-codes-desc') + this._backupCodesToHTML(response.backup_codes),
        oneAction: true
      }
    });
  }

  /**
   * Backup codes to HTML
   */
  private _backupCodesToHTML(backupCodes: string[]) {
    let ul = '<ul>';
    backupCodes.forEach(code => {
      ul += `<li>${ code }</li>`;
    });
    return ul + '</ul>';
  }
}
